import React, { useEffect, useMemo, useState } from 'react'
import { api } from '../../lib/api'
import type { BillingEvent, Organization } from '../../lib/types'

export default function AdminBillingLedger() {
  const [events, setEvents] = useState<BillingEvent[]>([])
  const [orgs, setOrgs] = useState<Organization[]>([])
  const [orgFilter, setOrgFilter] = useState('')
  const [eventTypeFilter, setEventTypeFilter] = useState('')
  const [includeDemo, setIncludeDemo] = useState(false)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    api(`/admin/organizations${includeDemo ? '?include_demo=true' : ''}`).then(setOrgs)
  }, [includeDemo])

  useEffect(() => {
    setLoading(true)
    const params = new URLSearchParams()
    if (orgFilter) params.set('organization_id', orgFilter)
    if (eventTypeFilter) params.set('event_type', eventTypeFilter)
    if (includeDemo) params.set('include_demo', 'true')
    params.set('limit', '200')
    api(`/admin/billing-events?${params.toString()}`).then((rows) => { setEvents(rows); setLoading(false) })
  }, [orgFilter, eventTypeFilter, includeDemo])

  const orgName = (id: string) => orgs.find((o) => o.id === id)?.name || `org ${id.slice(0, 8)}`

  const total = useMemo(() => events.reduce((sum, e) => sum + e.amount_cents, 0), [events])

  return (
    <div>
      <div className="page-head" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1>Billing ledger</h1>
          <div className="ph-sub">Cross-organization seat-activation and outcome-fee events. Manual ledger — no Stripe integration yet.</div>
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
          <input type="checkbox" checked={includeDemo} onChange={(e) => setIncludeDemo(e.target.checked)} />
          Include demo network
        </label>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        <div className="field" style={{ maxWidth: 240 }}>
          <label>Filter by organization</label>
          <select className="inp" value={orgFilter} onChange={(e) => setOrgFilter(e.target.value)}>
            <option value="">All organizations</option>
            {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
          </select>
        </div>
        <div className="field" style={{ maxWidth: 220 }}>
          <label>Filter by event type</label>
          <select className="inp" value={eventTypeFilter} onChange={(e) => setEventTypeFilter(e.target.value)}>
            <option value="">All event types</option>
            <option value="seat_activated">Seat activated</option>
            <option value="session_4_completed">Session 4 completed</option>
          </select>
        </div>
      </div>

      {loading ? (
        <div className="page-loading">Loading…</div>
      ) : (
        <>
          <div className="kpis" style={{ marginBottom: 14 }}>
            <div className="kpi-card"><span className="kpi-label">Events shown</span><b className="kpi-value">{events.length}</b></div>
            <div className="kpi-card"><span className="kpi-label">Total recorded</span><b className="kpi-value">${(total / 100).toFixed(2)}</b></div>
          </div>
          <div className="table-wrap">
            <table className="data-table">
              <thead><tr><th>Event</th><th>Organization</th><th>Provider</th><th>Amount</th><th>Status</th><th>Created</th></tr></thead>
              <tbody>
                {events.map((e) => (
                  <tr key={e.id}>
                    <td><span className="badge badge-blue">{e.event_type.replace(/_/g, ' ')}</span></td>
                    <td>{orgName(e.organization_id)}</td>
                    <td className="muted">{e.provider_id ? `provider ${e.provider_id.slice(0, 8)}` : '—'}</td>
                    <td>${(e.amount_cents / 100).toFixed(2)}</td>
                    <td>{e.status}</td>
                    <td className="muted">{new Date(e.created_at).toLocaleString()}</td>
                  </tr>
                ))}
                {events.length === 0 && <tr><td colSpan={6} className="muted">No billing events recorded yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}

      <p className="legal-note">
        These are real recorded ledger events (seat_activated / session_4_completed), not fabricated charges.
        No Stripe integration exists yet — nothing here has actually been billed.
      </p>
    </div>
  )
}
